import React, { useState, useEffect, useContext } from 'react';
import _ from 'lodash';
import type { DateRangeFormatFunction } from 'react-big-calendar';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import type { Moment } from 'moment';
import moment from 'moment';
import Color from 'color';
import Event from '@this/domain/appoint/event';
import type Calendar from '@this/domain/appoint/calendar';
import type SlotInfo from '@this/domain/appoint/slot_info';
import { CalendarWrapper, CalendarLoading, formats, messages } from '@this/components/appoint/common';
import EventPopupCard from '@this/components/appoint/shared/event_popup_card/event_popup_card';
import AppointContext from '@this/components/appoint/appoint_context';
import muiTheme from '@this/components/appoint/mui_theme';
import type { Holiday } from './dashboard';

const localizer = momentLocalizer(moment);

interface Props {
  eventLists: Event[][];
  calendars: Calendar[];
  selectedCalendars: Calendar[];
  date: Moment;
  holidays: Holiday[];
  isEventLoading: boolean;
  onDateChange: (date: Moment) => void;
  handleDetailOpen: (key: string) => void;
  onCreateEvent?: (event: Event) => void;
  onUpdateEvent?: (event: Event) => void;
  onDeleteEvent?: (event: Event) => void;
}

const DashboardBigCalendar = ({
  eventLists,
  calendars,
  selectedCalendars,
  date,
  holidays,
  isEventLoading,
  onDateChange,
  handleDetailOpen,
  onCreateEvent,
  onUpdateEvent,
  onDeleteEvent
}: Props) => {
  const contextValue = useContext(AppointContext);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [creatingEvent, setCreatingEvent] = useState<Event | null>(null);
  const [eventClickX, setEventClickX] = useState<number>(0);
  const [eventClickY, setEventClickY] = useState<number>(0);
  const [currentMouseX, setCurrentMouseX] = useState<number>(0);
  const [currentMouseY, setCurrentMouseY] = useState<number>(0);
  const [calendarScrollTo, setCalendarScrollTo] = useState<Date | undefined>(
    moment().set({ hour: 5, minute: 50 }).toDate()
  );
  const [selectedCalendarView, setSelectedCalendarView] = useState<'week' | 'day'>('week');
  const [touchStartedX, setTouchStartedX] = useState<number>(0);
  const [touchMovedX, setTouchMovedX] = useState<number>(0);
  const [isLongTapInfoShown, setIsLongTapInfoShown] = useState<boolean>(
    localStorage.getItem('isCalendarLongTapInfoShown') === 'true'
  );

  if (contextValue.isMobile) {
    const dayRangeHeaderFormat: DateRangeFormatFunction = ({ start }) => {
      const s = moment(start);
      return s.format('YYYY年MM月DD日(ddd) 週');
    };
    formats.dayRangeHeaderFormat = dayRangeHeaderFormat;
  }

  let events: Event[] = [];
  if (eventLists.length > 0) {
    events = eventLists.flat();
    if (creatingEvent) {
      events.push(creatingEvent);
    }
  }

  useEffect(() => {
    window.addEventListener('mousemove', e => {
      setCurrentMouseX(e.pageX);
      setCurrentMouseY(e.pageY);
    });
  }, []);

  const getEventProp = (e: Event) => {
    const calendar = _.find(calendars, c => c.id === e.calendarId);
    const eventProps = {
      className: e.end.valueOf() - e.start.valueOf() < 1000 * 60 * 45 ? 'short_event' : '',
      style: {}
    };
    let backgroundColor = new Color(
      e.adjustmentKey === null && calendar && calendar.backgroundColor
        ? calendar.backgroundColor
        : muiTheme.palette.primary.main
    );
    if (e.end < moment()) {
      if (e.adjustmentKey === null && calendar && calendar.backgroundColor) {
        const lightness = backgroundColor.lightness();
        const newLightness = 100 - (100 - lightness) / 5;
        backgroundColor = backgroundColor.lightness(newLightness);
      } else {
        backgroundColor = backgroundColor.lighten(0.5);
      }
    }
    if (e.adjustmentKey === null && calendar && calendar.backgroundColor) {
      const color = new Color('#ffffff');
      eventProps.style = {
        backgroundColor: backgroundColor.hex(),
        color: calendar.foregroundColor || '#1d1d1d',
        borderColor: color.darken(0.0).hex()
      };
      return eventProps;
    }
    // 調整中候補日は破線ボーダー
    if (e.adjustmentStatus && e.adjustmentStatus === 'adjusting') {
      eventProps.style = {
        backgroundColor: '#fff',
        color: muiTheme.palette.primary.main,
        border: `dashed 2px ${muiTheme.palette.primary.main}`
      };
      return eventProps;
    }
    // 確定済候補日はメインカラー
    eventProps.style = {
      backgroundColor: muiTheme.palette.primary.main,
      color: '#fff',
      borderColor: new Color(muiTheme.palette.primary.main).darken(0.3).hex()
    };
    return eventProps;
  };

  const getDayProp = (date: Moment) => {
    return {
      className: _.map(holidays, h => h.date).indexOf(date.format('YYYY-MM-DD')) > -1 ? 'holiday' : ''
    };
  };

  const handleSelectEvent = (event: Event, e: React.SyntheticEvent<HTMLElement, globalThis.Event>) => {
    e.stopPropagation();
    if (selectedEvent?.id === event.id) {
      setSelectedEvent(null);
      return;
    }
    if (event.adjustmentKey) {
      handleDetailOpen(event.adjustmentKey);
      return;
    }
    setSelectedEvent(event);
    const click = e.nativeEvent as MouseEvent;
    setEventClickX(click.pageX - 550 > 0 ? click.pageX - 550 : 0);
    setEventClickY(click.pageY - 450 > 0 ? click.pageY - 450 : 0);
  };

  const handleRangeChange = (val: any) => {
    setSelectedCalendarView(val.length === 1 ? 'day' : 'week');
    setCalendarScrollTo(moment().set({ hour: 5, minute: 50 }).toDate());
  };

  const handleSelectSlot = (slot: SlotInfo) => {
    setSelectedEvent(null);
    if (slot.start === slot.end) {
      // 終日の選択は無視
      return;
    }

    // デフォルトの時間を60分にする
    const minutes = 60;
    const durationMs = minutes * 60 * 1000;
    const start = moment(slot.start);
    let end = moment(slot.end);
    if (end.diff(start) < durationMs) {
      end = start.clone();
      end.add(minutes, 'minutes');
    }

    const event = new Event({
      name: '',
      start,
      end,
      calendarId: selectedCalendars[0].id,
      participants: selectedCalendars,
      description: '',
      meetingType: 'none'
    });
    setEventClickX(currentMouseX - 550 > 0 ? currentMouseX - 550 : 0);
    setEventClickY(currentMouseY - 450 > 0 ? currentMouseY - 450 : 0);
    setSelectedEvent(event);
    setCreatingEvent(event);
  };

  const handleClosePopup = () => {
    setSelectedEvent(null);
    setCreatingEvent(null);
  };

  const handleTouchStart = (e: any) => {
    setTouchStartedX(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: any) => {
    setTouchMovedX(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStartedX !== 0 && touchMovedX !== 0) {
      const diff = selectedCalendarView === 'day' ? 1 : 7;
      if (touchStartedX - touchMovedX > 150) {
        onDateChange(date.add(diff, 'days'));
      }

      if (touchStartedX - touchMovedX < -150) {
        onDateChange(date.subtract(diff, 'days'));
      }
    }
    setTouchStartedX(0);
    setTouchMovedX(0);
  };

  const handleCalendarMarginTap = (e: any) => {
    // カレンダー欄外(上部のボタン群等)のタップでは発火させないようにするため
    const outside = ['BUTTON', 'SPAN'].indexOf(e.target.tagName) > -1;
    if (!contextValue.isMobile || outside || isLongTapInfoShown) {
      return;
    }

    contextValue.setSnackbarContent({
      message: 'カレンダーの空欄をロングタップで予定を追加',
      severity: 'info'
    });
    contextValue.setOpenSnackbar(true);
    setIsLongTapInfoShown(true);
    localStorage.setItem('isCalendarLongTapInfoShown', 'true');
  };

  return (
    <>
      <EventPopupCard
        event={selectedEvent}
        x={eventClickX}
        y={eventClickY}
        onClose={() => handleClosePopup()}
        onCreate={event => onCreateEvent && onCreateEvent(event)}
        onUpdate={event => onUpdateEvent && onUpdateEvent(event)}
        onDelete={event => onDeleteEvent && onDeleteEvent(event)}
      />
      <CalendarWrapper
        isMobile={contextValue.isMobile}
        onClick={e => handleCalendarMarginTap(e)}
        onTouchStart={e => handleTouchStart(e)}
        onTouchMove={e => handleTouchMove(e)}
        onTouchEnd={() => handleTouchEnd()}
      >
        {isEventLoading && <CalendarLoading />}
        <BigCalendar
          localizer={localizer}
          events={events}
          titleAccessor={event => event.name || '(タイトルなし)'}
          startAccessor={event => event.start.toDate()}
          endAccessor={event => event.end.toDate()}
          selectable
          longPressThreshold={300}
          onRangeChange={val => handleRangeChange(val)}
          onSelectEvent={(event, e) => handleSelectEvent(event, e)}
          onSelectSlot={slot => handleSelectSlot(slot)}
          defaultView="week"
          views={['day', 'week']}
          date={date.toDate()}
          eventPropGetter={e => getEventProp(e)}
          dayPropGetter={date => getDayProp(moment(date))}
          onNavigate={date => {
            onDateChange(moment(date));
          }}
          formats={formats}
          scrollToTime={calendarScrollTo}
          messages={messages}
        />
      </CalendarWrapper>
    </>
  );
};

export default DashboardBigCalendar;
